import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, FlaskConical } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { missingText } from "@/components/rotation/NewModelMinutes";

const f1 = (v) => (v == null ? "—" : Number(v).toFixed(1));
const COLS = [["min", "MIN"], ["pts", "PTS"], ["reb", "REB"], ["ast", "AST"], ["fg3m", "3PM"], ["pra", "PRA"]];
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { timeZone: "Europe/Athens", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

function Rows({ totals, code }) {
  return [["starters", "STARTERS"], ["bench", "BENCH"], ["team", "TOTAL"]].map(([k, l]) => (
    <tr key={k} className={k === "team" ? "is-total" : ""} data-testid={`tb-${code}-${k}`}>
      <td>{l}</td>{COLS.map(([c]) => <td key={c}>{totals ? f1(totals[k][c]) : "—"}</td>)}
    </tr>
  ));
}

function TeamBlock({ t, actual, gameId, gameStatus, day }) {
  const [open, setOpen] = useState(false);
  const hs = t.hist_split, ps = t.proj_split;
  return (
    <div className="tb-team" data-testid={`tb-team-${t.team_code}`}>
      <div className="tb-team-head">
        <Link to={`/teams/${t.team_id}`} className="tb-code" data-testid={`tb-team-link-${t.team_code}`}>{t.team_code}</Link>
        <span className={`tb-chip ${t.status === "complete" ? "ok" : "warn"}`} data-testid={`tb-status-${t.team_code}`}>{t.status === "complete" ? "240/240" : "PARTIAL"}</span>
        {hs && <span className="tb-split">Season S {f1(hs.starter_minutes)} / B {f1(hs.bench_minutes)} · Projected S {f1(ps?.starter_minutes)} / B {f1(ps?.bench_minutes)}</span>}
        <Link to={`/rotations?team=${t.team_id}`} className="tb-rotation-link" data-testid={`tb-rotation-${t.team_code}`}>Rotation</Link>
      </div>
      {t.missing?.length > 0 && (
        <p className="tb-missing" data-testid={`tb-missing-${t.team_code}`}>Missing: {missingText(t.missing)} — {t.missing.map((m) => m.name).join(", ")}.{" "}
          {gameStatus === "pre" && <Link to={`/rotation?day=${day || ""}&game=${gameId}&team=${t.team_id}`} data-testid={`tb-fill-${t.team_code}`}>Fill minutes</Link>}</p>
      )}
      {t.reason && !t.missing?.length && <p className="tb-note">{t.reason}</p>}
      <div className="tb-wrap"><table className="tb-table">
        <thead><tr><th>{actual ? "FORECAST" : ""}</th>{COLS.map(([, l]) => <th key={l}>{l}</th>)}</tr></thead>
        <tbody><Rows totals={t.totals} code={t.team_code} /></tbody>
        {actual && <tbody className="tb-actual"><tr><td colSpan={7}>ACTUAL BOX SCORE</td></tr><Rows totals={actual.totals} code={`${t.team_code}-actual`} /></tbody>}
      </table></div>
      <button type="button" className="tb-expand" onClick={() => setOpen(!open)} data-testid={`tb-expand-${t.team_code}`}>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}{open ? "Hide" : "Show"} contributing players ({t.players.filter((p) => p.available).length})
      </button>
      {open && (
        <div className="tb-wrap"><table className="tb-table tb-players" data-testid={`tb-players-${t.team_code}`}>
          <tbody>{t.players.map((p) => (
            <tr key={p.player_id} className={p.available ? "" : "is-out"}>
              <td><Link to={`/player/${p.player_id}`}>{p.name}</Link><small>{p.starter ? "S" : "B"}{p.out ? " · OUT" : !p.available ? " · no rate" : ""}{p.tier_used ? ` · ${p.tier_used}` : ""}</small></td>
              {COLS.map(([c]) => <td key={c}>{f1(c === "min" ? p.minutes : p[c])}</td>)}
            </tr>
          ))}</tbody>
        </table></div>
      )}
    </div>
  );
}

export function TeamBreakdown({ game }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setD(null); setErr("");
    api.get("/season-model/team-breakdown", { params: { game_id: game.id } }).then((r) => setD(r.data)).catch((e) => setErr(errorText(e)));
  }, [game.id]);
  const run = d?.run;
  const m = run?.matchup;
  return (
    <section className="tb-root" data-testid="tp-team-breakdown">
      <header className="tb-head">
        <h3><FlaskConical size={15} />TEAM BREAKDOWN <em>NEW MODEL · SHADOW</em></h3>
        {run && <small data-testid="tb-meta">{run.model_version} · cutoff {when(run.data_cutoff_utc)} · version {d.versions}</small>}
      </header>
      {err && <p className="tb-note">{err}</p>}
      {!d && !err && <p className="tb-note">Loading…</p>}
      {d && !run && <p className="tb-note" data-testid="tb-none">No pre-game run was saved for this game.</p>}
      {d && !run && d.actual?.map((a) => (
        <div key={a.team_id} className="tb-team" data-testid={`tb-actual-${a.team_code}`}>
          <div className="tb-team-head"><Link to={`/teams/${a.team_id}`} className="tb-code">{a.team_code}</Link><span className="tb-chip ok">ACTUAL</span></div>
          <div className="tb-wrap"><table className="tb-table"><thead><tr><th>ACTUAL</th>{COLS.map(([, l]) => <th key={l}>{l}</th>)}</tr></thead>
            <tbody><Rows totals={a.totals} code={`${a.team_code}-actual`} /></tbody></table></div>
        </div>
      ))}
      {m && (
        <div className="tb-matchup" data-testid="tb-matchup">
          <div><small>{game.away_code}</small><b>{f1(m.away_projected_pts)}</b></div>
          <div><small>{game.home_code}</small><b>{f1(m.home_projected_pts)}</b></div>
          <div><small>GAME TOTAL</small><b data-testid="tb-game-total">{f1(m.game_total)}</b></div>
          <div><small>MARGIN ({game.home_code})</small><b>{m.home_margin > 0 ? "+" : ""}{f1(m.home_margin)}</b></div>
        </div>
      )}
      {run && !m && <p className="tb-note">Score, total and margin only show when both teams have a complete 240-minute rotation.</p>}
      {run?.teams.map((t) => <TeamBlock key={t.team_id} t={t} actual={d.actual?.find((a) => a.team_id === t.team_id)} gameId={game.id} gameStatus={d.game_status} day={game.day} />)}
      {run && <p className="tb-note">Team values are the exact sum of the player rows (starters + bench = total). No win, spread or over/under probabilities are derived from mean scores.</p>}
    </section>
  );
}
